"use client"

import { useCallback } from "react"
import {
  type InfiniteData,
  type QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import {
  deliveryShipmentDetailQueryKey,
  deliveryShipmentsQueryPrefix,
  type DeliveryShipmentStatus,
} from "@/hooks/queries/delivery-shipments"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  markDeliveryCrmHandoffVerifiedAction,
  reconcileUnknownDeliveryShipmentAction,
  retryDeliveryCrmHandoffAction,
  type DeliveryShipmentDetail,
  type DeliveryShipmentListPage,
  type MarkDeliveryCrmHandoffVerifiedInput,
  type ReconcileUnknownShipmentInput,
  type RetryDeliveryCrmHandoffInput,
} from "@/server-actions/delivery-shipments"
import { useTranslations } from "next-intl"


type FormErrorSetter = (errors: Record<string, unknown>) => void

export type ReconcileDeliveryShipmentMutationInput =
  ReconcileUnknownShipmentInput & { setErrors?: FormErrorSetter }

export function useReconcileDeliveryShipmentMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryShipments")

  const updateShipment = useDeliveryShipmentCacheUpdater()

  return useMutation({
    mutationFn: async ({ setErrors, ...input }:
      ReconcileDeliveryShipmentMutationInput): Promise<DeliveryShipmentDetail> => {
      const result = await withQueryHandling(
        reconcileUnknownDeliveryShipmentAction(input),
        {
          loading: tUi("recordingTheVerifiedNovaPostWaybill"),
          success: tUi("unknownShipmentReconciled"),
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: updateShipment,
  })
}

export function useRetryDeliveryCrmHandoffMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryShipments")

  const updateShipment = useDeliveryShipmentCacheUpdater()

  return useMutation({
    mutationFn: async (
      input: RetryDeliveryCrmHandoffInput,
    ): Promise<DeliveryShipmentDetail> => {
      const result = await withQueryHandling(
        retryDeliveryCrmHandoffAction(input),
        {
          loading: tUi("retryingTheCRMWorkerHandoff"),
          success: tUi("cRMHandoffProcessed"),
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: updateShipment,
  })
}

export function useMarkDeliveryCrmHandoffVerifiedMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryShipments")

  const updateShipment = useDeliveryShipmentCacheUpdater()

  return useMutation({
    mutationFn: async (
      input: MarkDeliveryCrmHandoffVerifiedInput,
    ): Promise<DeliveryShipmentDetail> => {
      const result = await withQueryHandling(
        markDeliveryCrmHandoffVerifiedAction(input),
        {
          loading: tUi("recordingTheVerifiedCRMNote"),
          success: tUi("cRMHandoffMarkedAsVerified"),
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: updateShipment,
  })
}

function useDeliveryShipmentCacheUpdater() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useCallback((shipment: DeliveryShipmentDetail) => {
    queryClient.setQueryData(
      deliveryShipmentDetailQueryKey(tenantId, shipment.id),
      shipment,
    )

    const prefix = deliveryShipmentsQueryPrefix(tenantId)
    for (const query of queryClient.getQueryCache().findAll({ queryKey: prefix })) {
      const status = getCachedStatus(query.queryKey)
      queryClient.setQueryData<InfiniteData<DeliveryShipmentListPage>>(
        query.queryKey,
        current => updateShipmentPages(current, shipment, status),
      )
    }
  }, [queryClient, tenantId])
}

function getCachedStatus(queryKey: QueryKey): DeliveryShipmentStatus | undefined {
  const filter = queryKey[2]
  if (!filter || typeof filter !== "object" || !("status" in filter)) {
    return undefined
  }
  const status = filter.status
  return status === "creating"
    || status === "created"
    || status === "unknown"
    || status === "failed"
    || status === "cancelled"
    ? status
    : undefined
}

function updateShipmentPages(
  current: InfiniteData<DeliveryShipmentListPage> | undefined,
  shipment: DeliveryShipmentDetail,
  status?: DeliveryShipmentStatus,
): InfiniteData<DeliveryShipmentListPage> | undefined {
  if (!current) return current
  return {
    ...current,
    pages: current.pages.map(page => ({
      ...page,
      shipments: page.shipments.flatMap(item => {
        if (item.id !== shipment.id) return [item]
        if (status && status !== shipment.operationStatus) return []
        return [shipment]
      }),
    })),
  }
}
